import { useCallback, useEffect, useState } from 'react';
import {
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  MessageSquare,
  UserCheck,
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import {
  getIncidents,
  solveAllIncidents,
  solveIncident,
  takeIncident,
  updateIncidentNotes,
} from '../api/api';
import { ActionDrawer, ConfirmDialog, Toast } from '../components/ui';
import { getUser } from '../utils/auth';
import styles from './Incidents.module.scss';

const statuses = [
  { id: '', label: 'All incidents', stat: 'total' },
  { id: 'untaken', label: 'Needs owner', stat: 'untaken' },
  { id: 'taken', label: 'In progress', stat: 'taken' },
  { id: 'solved', label: 'Solved', stat: 'solved' },
];

function Incidents() {
  const { machineId: machineIdParam } = useParams();
  const machineId = machineIdParam ? parseInt(machineIdParam, 10) : null;
  const isAdmin = getUser()?.role === 'admin';
  const [incidents, setIncidents] = useState([]);
  const [pagination, setPagination] = useState({});
  const [stats, setStats] = useState({ total: 0, untaken: 0, taken: 0, solved: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [editingIncident, setEditingIncident] = useState(null);
  const [noteValue, setNoteValue] = useState('');
  const [confirmSolveAll, setConfirmSolveAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchIncidents = useCallback(async () => {
    if (!machineId) {
      return;
    }
    try {
      setLoading(true);
      const response = await getIncidents(currentPage, 20, statusFilter || null, machineId);
      setIncidents(response.data?.incidents || []);
      setPagination(response.data?.pagination || {});
      setError('');
    } catch {
      setError('Failed to load incidents.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, machineId, statusFilter]);

  const fetchStats = useCallback(async () => {
    if (!machineId) {
      return;
    }
    try {
      const [untakenResponse, takenResponse, solvedResponse] = await Promise.all([
        getIncidents(1, 1, 'untaken', machineId),
        getIncidents(1, 1, 'taken', machineId),
        getIncidents(1, 1, 'solved', machineId),
      ]);
      const untaken = untakenResponse.data?.pagination?.total_count || 0;
      const taken = takenResponse.data?.pagination?.total_count || 0;
      const solved = solvedResponse.data?.pagination?.total_count || 0;
      setStats({ total: untaken + taken + solved, untaken, taken, solved });
    } catch {
      // The incident list remains usable if summary counts are temporarily unavailable.
    }
  }, [machineId]);

  useEffect(() => {
    fetchIncidents();
    fetchStats();
  }, [fetchIncidents, fetchStats]);

  const refresh = async message => {
    if (message) {
      setSuccess(message);
    }
    await Promise.all([fetchIncidents(), fetchStats()]);
  };

  const handleTake = async incident => {
    try {
      setSaving(true);
      await takeIncident(incident.id);
      await refresh(`Incident ${incident.id} assigned to you.`);
    } catch {
      setError('Failed to take incident.');
    } finally {
      setSaving(false);
    }
  };

  const handleSolve = async incident => {
    try {
      setSaving(true);
      await solveIncident(incident.id);
      await refresh(`Incident ${incident.id} marked solved.`);
    } catch {
      setError('Failed to solve incident.');
    } finally {
      setSaving(false);
    }
  };

  const handleSolveAll = async () => {
    try {
      setSaving(true);
      await solveAllIncidents(machineId);
      setConfirmSolveAll(false);
      await refresh('All active incidents marked solved.');
    } catch {
      setError('Failed to solve all incidents.');
    } finally {
      setSaving(false);
    }
  };

  const openNotes = incident => {
    setEditingIncident(incident);
    setNoteValue(incident.notes || '');
  };

  const saveNotes = async () => {
    try {
      setSaving(true);
      await updateIncidentNotes(editingIncident.id, noteValue);
      setEditingIncident(null);
      setNoteValue('');
      await refresh('Incident note saved.');
    } catch {
      setError('Failed to update incident note.');
    } finally {
      setSaving(false);
    }
  };

  const selectStatus = status => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const statusLabel = status =>
    ({ untaken: 'Needs owner', taken: 'In progress', solved: 'Solved' })[status] || status;
  const formatDate = value => new Date(value).toLocaleString();

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>Machine monitoring</span>
          <h1>Incident queue</h1>
          <p>
            Work from unowned alerts through investigation to resolution, with notes kept on the
            incident.
          </p>
        </div>
        {isAdmin && (
          <button
            type="button"
            className={styles.solveAllButton}
            onClick={() => setConfirmSolveAll(true)}
            disabled={saving || stats.untaken + stats.taken === 0}
          >
            <CheckCheck size={16} /> Resolve all incidents
          </button>
        )}
      </header>
      {error && (
        <Toast tone="error" onClose={() => setError('')}>
          {error}
        </Toast>
      )}
      {success && (
        <Toast tone="success" onClose={() => setSuccess('')}>
          {success}
        </Toast>
      )}
      <ConfirmDialog
        open={confirmSolveAll}
        title="Resolve all active incidents?"
        description={`This marks ${stats.untaken + stats.taken} unowned or in-progress incidents as solved.`}
        confirmLabel="Resolve all"
        tone="primary"
        busy={saving}
        onConfirm={handleSolveAll}
        onCancel={() => setConfirmSolveAll(false)}
      />

      <nav className={styles.statusSummary} aria-label="Filter incidents by status">
        {statuses.map(status => (
          <button
            type="button"
            key={status.stat}
            className={statusFilter === status.id ? styles.statusActive : ''}
            onClick={() => selectStatus(status.id)}
          >
            <span>{status.label}</span>
            <strong>{stats[status.stat]}</strong>
          </button>
        ))}
      </nav>

      <section className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>{statuses.find(status => status.id === statusFilter)?.label}</h2>
            <p>
              {loading
                ? 'Loading incidents…'
                : `${pagination.total_count ?? incidents.length} incidents in this view`}
            </p>
          </div>
        </div>
        {!loading && incidents.length ? (
          <div className={styles.incidentList}>
            {incidents.map(incident => (
              <article className={styles.incidentCard} key={incident.id}>
                <div className={styles.incidentHeading}>
                  <span className={`${styles.statusBadge} ${styles[incident.status]}`}>
                    {statusLabel(incident.status)}
                  </span>
                  <strong>{incident.error_type}</strong>
                  <time>{formatDate(incident.created_at)}</time>
                </div>
                <p className={styles.incidentMessage}>{incident.error_message}</p>
                <div className={styles.incidentMeta}>
                  {incident.owner ? (
                    <span>
                      <UserCheck size={14} /> Owned by {incident.owner}
                    </span>
                  ) : (
                    <span>
                      <CircleAlert size={14} /> Waiting for an owner
                    </span>
                  )}
                  {incident.notes && (
                    <button type="button" onClick={() => openNotes(incident)}>
                      <MessageSquare size={14} />
                      <span>{incident.notes}</span>
                    </button>
                  )}
                </div>
                <div className={styles.incidentActions}>
                  {incident.status !== 'untaken' && incident.owner && (
                    <button
                      type="button"
                      className={styles.noteButton}
                      onClick={() => openNotes(incident)}
                    >
                      <MessageSquare size={14} /> {incident.notes ? 'Edit note' : 'Add note'}
                    </button>
                  )}
                  {incident.status === 'untaken' && (
                    <button
                      type="button"
                      className={styles.primaryAction}
                      onClick={() => handleTake(incident)}
                      disabled={saving}
                    >
                      Take ownership
                    </button>
                  )}
                  {incident.status === 'taken' && (
                    <button
                      type="button"
                      className={styles.primaryAction}
                      onClick={() => handleSolve(incident)}
                      disabled={saving}
                    >
                      <CheckCheck size={14} /> Mark solved
                    </button>
                  )}
                  {incident.status === 'solved' && (
                    <span className={styles.completedText}>
                      <CheckCheck size={14} /> Complete
                    </span>
                  )}
                </div>
              </article>
            ))}
          </div>
        ) : (
          !loading && (
            <div className={styles.emptyState}>
              <CircleAlert size={26} />
              <strong>No incidents in this view</strong>
              <p>Choose another status to review the rest of the queue.</p>
            </div>
          )
        )}
        {pagination.total_pages > 1 && (
          <div className={styles.pagination}>
            <button
              type="button"
              onClick={() => setCurrentPage(page => page - 1)}
              disabled={!pagination.has_prev}
            >
              <ChevronLeft size={15} /> Previous
            </button>
            <span>
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage(page => page + 1)}
              disabled={!pagination.has_next}
            >
              Next <ChevronRight size={15} />
            </button>
          </div>
        )}
      </section>

      <ActionDrawer
        open={Boolean(editingIncident)}
        eyebrow="Incident investigation"
        title="Investigation note"
        description={
          editingIncident ? `${editingIncident.error_type} · Incident ${editingIncident.id}` : ''
        }
        onClose={() => setEditingIncident(null)}
        closeDisabled={saving}
        footer={
          <>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => setEditingIncident(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={saveNotes}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save note'}
            </button>
          </>
        }
      >
        <div className={styles.noteEditor}>
          {editingIncident && (
            <div className={styles.noteContext}>
              <span>Status</span>
              <strong>{statusLabel(editingIncident.status)}</strong>
              <span>Owner</span>
              <strong>{editingIncident.owner || 'Unassigned'}</strong>
            </div>
          )}
          <label>
            <span>Operator note</span>
            <textarea
              value={noteValue}
              onChange={event => setNoteValue(event.target.value)}
              placeholder="Record findings, actions, and follow-up…"
              autoFocus
            />
          </label>
        </div>
      </ActionDrawer>
    </div>
  );
}

export default Incidents;
